import type { ReactNode } from 'react'
import { VpButton, VpLoader } from '@vtmn-play/react'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

const EmojiPicker = lazy(() => import('emoji-picker-react'))
export const CHECK_EMOJI_UNIFIED = '2714-fe0f'
export const DEFAULT_FOLDER_EMOJI_UNIFIED = '1f642'
const FolderEmojiRenderer = lazy(() =>
  import('./FavoriteViewEmojiRenderer').then(({ FavoriteViewEmojiRenderer }) => ({ default: FavoriteViewEmojiRenderer })),
)

interface FavoriteViewEmojiProps {
  unified: string | null | undefined
  fallback?: ReactNode
}

export function FavoriteViewEmoji({ unified, fallback }: Readonly<FavoriteViewEmojiProps>) {
  return (
    <Suspense fallback={fallback ?? <span className="favorite-view-panel__folder-emoji" aria-hidden="true" />}>
      <FolderEmojiRenderer unified={unified} size={32} />
    </Suspense>
  )
}

interface FavoriteViewEmojiPickerProps {
  selectedEmoji: string | null
  onSelect: (unified: string) => void
}

interface FavoriteViewEmojiPickerFallbackProps {
  loadingLabel: string
}

function FavoriteViewEmojiPickerFallback({ loadingLabel }: Readonly<FavoriteViewEmojiPickerFallbackProps>) {
  return (
    <output className="favorite-view-panel__emoji-picker-loading" aria-label={loadingLabel}>
      <div className="favorite-view-panel__emoji-picker-spinner">
        <VpLoader size="small" aria-hidden="true" />
      </div>
      <div className="favorite-view-panel__emoji-picker-skeleton" aria-hidden="true">
        {Array.from({ length: 35 }, (_placeholder, placeholderIndex) => (
          <span key={placeholderIndex} />
        ))}
      </div>
    </output>
  )
}

function getPickerPosition(trigger: HTMLElement, drawer: HTMLElement) {
  const triggerBounds = trigger.getBoundingClientRect()
  const drawerBounds = drawer.getBoundingClientRect()
  const pickerHeight = 450
  const belowTrigger = window.innerHeight - triggerBounds.bottom
  const top = belowTrigger >= pickerHeight + 8
    ? triggerBounds.bottom - drawerBounds.top + 8
    : triggerBounds.top - drawerBounds.top - pickerHeight - 8

  return {
    top: Math.max(8, top),
    left: Math.max(8, triggerBounds.left - drawerBounds.left),
  }
}

export function FavoriteViewEmojiPicker({ selectedEmoji, onSelect }: Readonly<FavoriteViewEmojiPickerProps>) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [pickerContainer, setPickerContainer] = useState<HTMLElement | null>(null)
  const [pickerPosition, setPickerPosition] = useState<{ top: number, left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const pickerRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (!isOpen)
      return

    const trigger = triggerRef.current
    const drawer = pickerContainer
    if (!trigger || !drawer)
      return

    const updatePosition = () => {
      setPickerPosition(getPickerPosition(trigger, drawer))
    }
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node))
        return

      if (trigger.contains(event.target) || pickerRef.current?.contains(event.target))
        return

      setIsOpen(false)
    }

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    document.addEventListener('pointerdown', handleOutsidePointerDown)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      document.removeEventListener('pointerdown', handleOutsidePointerDown)
    }
  }, [isOpen, pickerContainer])

  const togglePicker = () => {
    if (isOpen) {
      setIsOpen(false)
      return
    }

    const trigger = triggerRef.current
    const drawer = trigger?.closest<HTMLElement>('.favorite-view-panel__dialog')
    if (!trigger || !drawer)
      return

    setPickerContainer(drawer)
    setPickerPosition(getPickerPosition(trigger, drawer))
    setIsOpen(true)
  }

  return (
    <>
      <VpButton
        ref={triggerRef}
        type="button"
        variant="tertiary"
        className="favorite-view-panel__emoji-trigger"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={t('favorite-views.panel.choose-emoji')}
        onClick={togglePicker}
        data-testid="favorite-folder-emoji"
      >
        <FavoriteViewEmoji
          unified={selectedEmoji ?? DEFAULT_FOLDER_EMOJI_UNIFIED}
          fallback={<span aria-hidden="true">{selectedEmoji ? '📁' : '🙂'}</span>}
        />
      </VpButton>
      {isOpen && pickerContainer && pickerPosition && createPortal(
        <dialog
          ref={pickerRef}
          open
          className="favorite-view-panel__emoji-picker"
          style={pickerPosition}
          aria-label={t('favorite-views.panel.choose-emoji')}
          data-testid="favorite-view-emoji-picker"
        >
          <Suspense
            fallback={<FavoriteViewEmojiPickerFallback loadingLabel={t('favorite-views.panel.loading-emoji')} />}
          >
            <EmojiPicker
              lazyLoadEmojis
              onEmojiClick={(selection) => {
                onSelect(selection.unified)
                setIsOpen(false)
              }}
            />
          </Suspense>
        </dialog>,
        pickerContainer,
      )}
    </>
  )
}
